import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LabelList } from 'recharts';
import { Globe, MonitorPlay, Info } from 'lucide-react';
import { Tooltip as Hint } from '@parrot/ui';
import { Select, PlatformIcon } from '@/components/primitives';
import { formatUsd, formatUsdCompact, prettyQuarter, shortPlatform } from '@/lib/format';
import {
  type TitleRevenueRow,
  type Breakdown,
  type RevenueMetric,
  buildStackedSeries,
  colorForRegion,
  colorForPlatform,
  allRegions,
  allPlatforms,
  fetchRankWindow,
} from '@/lib/titleCharts';
import { SegmentedControl } from './SegmentedControl';
import { QuarterRangeSlider } from './QuarterRangeSlider';
import { ChartSummary, Emph } from './ChartSummary';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { NeighborRankTable } from './NeighborRankTable';
import { useResolvedColors } from '@/lib/useResolvedColor';
import styles from './RevenueOverTimeChart.module.css';

export interface RevenueOverTimeChartProps {
  id: string;
  title: string;
  revenue: TitleRevenueRow[];
}

const WINDOW = 5; // titles shown in the neighbor table (2 above / this / 2 below)

/* Value labels (d360 draws a total above every bar and values inside segments).
   d360's default window is ~8 quarters; ours can be 26, where labels on every
   bar collide. So: all labels up to this many bars, else only the latest total. */
const LABEL_ALL_BARS_MAX = 12;
/** Smallest segment (px) that can hold an inside value label. */
const SEGMENT_LABEL_MIN_H = 18;

const BREAKDOWN_OPTS = [
  { value: 'region', label: 'Region' },
  { value: 'platform', label: 'Platform' },
];
const METRIC_OPTS = [
  { value: 'cumulative', label: 'Cumulative' },
  { value: 'quarterly', label: 'Quarterly' },
];

/**
 * Revenue Over Time — full parity with d360's RevenueOverTimeWidget, rebuilt in
 * Recharts. Uses the app's shared `Select` (same selectors as the Leaderboard,
 * single-select with an "All X" option) for region/platform/breakdown/metric,
 * plus a quarter range slider, stacked bars with Not-Available hatching, a
 * narrative summary, and a real neighbor rank table from the leaderboard.
 */
export function RevenueOverTimeChart({ id, title, revenue }: RevenueOverTimeChartProps) {
  const regionOptions = useMemo(() => allRegions(revenue), [revenue]);
  const platformOptions = useMemo(() => allPlatforms(revenue), [revenue]);

  const [breakdown, setBreakdown] = useState<Breakdown>('region');
  const [metric, setMetric] = useState<RevenueMetric>('cumulative');
  // Single-select scope ('' = All), matching the Leaderboard convention.
  const [regionFilter, setRegionFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  // Inclusive quarter window [start, end] over the full quarter list.
  const [range, setRange] = useState<[number, number] | null>(null);

  // '' (All) → every value; otherwise the single picked value.
  const selectedRegions = regionFilter ? [regionFilter] : regionOptions;
  const selectedPlatforms = platformFilter ? [platformFilter] : platformOptions;

  const regionSelectOptions = [
    { value: '', label: 'All Regions' },
    ...regionOptions.map((r) => ({ value: r, label: r })),
  ];
  const platformSelectOptions = [
    { value: '', label: 'All Platforms' },
    ...platformOptions.map((p) => ({
      value: p,
      label: shortPlatform(p),
      icon: <PlatformIcon name={p} size={18} />,
    })),
  ];
  /** Series names are platform names in platform breakdown — show them short. */
  const seriesLabel = (name: string) => (breakdown === 'platform' ? shortPlatform(name) : name);

  const { points: allPoints, series, notAvailable } = useMemo(
    () =>
      buildStackedSeries(revenue, {
        breakdown,
        metric,
        regions: selectedRegions,
        platforms: selectedPlatforms,
      }),
    [revenue, breakdown, metric, selectedRegions, selectedPlatforms],
  );

  // Range window.
  const maxIdx = Math.max(0, allPoints.length - 1);
  const [lo, hi] = range ?? [0, maxIdx];
  const points = allPoints.slice(lo, hi + 1);
  const allLabels = allPoints.map((p) => p.label);

  const colorFor = (name: string, i: number) =>
    breakdown === 'region' ? colorForRegion(name) : colorForPlatform(name, i);
  const seriesTokens = useMemo(() => series.map((name, i) => colorFor(name, i)), [series, breakdown]);
  const seriesColors = useResolvedColors(seriesTokens);
  const colorAt = (i: number) => seriesColors[i] ?? seriesTokens[i];
  const [naColor] = useResolvedColors(['var(--text-disabled)']);

  const windowQuarters = points.map((p) => p.quarter);
  const lastQ = windowQuarters[windowQuarters.length - 1] ?? '';

  /* Headline figure for the summary sentence, matching what the chart shows:
     cumulative → running total at the last visible quarter (which counts from the
     first quarter of data, so the sentence says "from <first data quarter>");
     quarterly → that quarter's own revenue. */
  const firstDataQ = allPoints[0]?.quarter ?? '';
  const lastPoint = points[points.length - 1];
  const headlineTotal = lastPoint
    ? series.reduce((acc, name) => acc + (Number(lastPoint[name]) || 0), 0)
    : 0;

  // Neighbor table scope: only pin a platform when the user explicitly selected one.
  // If "All Platforms" is selected (platformFilter === ''), send no platform param
  // so the BE aggregates across all platforms — otherwise we'd rank against Netflix-only.
  const scopePlatform = platformFilter || undefined;

  // Derive timePeriod / endDate from the current range + metric selection.
  // Cumulative = all-time rank; Quarterly = latest quarter rank (or the slider's last quarter).
  const isFullRange = range === null || (lo === 0 && hi === maxIdx);
  const rankEndDate = !isFullRange && points.length ? points[points.length - 1].quarter : undefined;
  const rankTimePeriod: 'all' | 'latest' = metric === 'cumulative' ? 'all' : 'latest';

  const { data: rankWindow, isLoading: rankLoading } = useQuery({
    queryKey: ['rank-window', id, scopePlatform, selectedRegions, rankTimePeriod, rankEndDate],
    queryFn: () =>
      fetchRankWindow(id, {
        platform: scopePlatform,
        region: regionFilter ? [regionFilter] : undefined,
        timePeriod: rankTimePeriod,
        endDate: rankEndDate,
        window: WINDOW,
      }),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  const neighborRows = rankWindow?.rows ?? [];
  const maxRevenue = neighborRows.reduce((m, r) => Math.max(m, r.revenue_contribution_usd), 0);
  /* d360 scopes the rank panel by time only (platform/region already sit in the
     header selects): cumulative → the quarter span; quarterly → the one quarter. */
  const rankLastQ = rankEndDate ?? allPoints[allPoints.length - 1]?.quarter;
  const scopeLabel =
    metric === 'cumulative'
      ? firstDataQ && rankLastQ
        ? `${prettyQuarter(firstDataQ)} – ${prettyQuarter(rankLastQ)}`
        : undefined
      : rankLastQ
        ? prettyQuarter(rankLastQ)
        : undefined;

  const showAllLabels = points.length <= LABEL_ALL_BARS_MAX;
  const lastLabel = points[points.length - 1]?.label;
  const totalAt = (label: string) => {
    const p = points.find((pt) => pt.label === label);
    return p ? series.reduce((acc, name) => acc + (Number(p[name]) || 0), 0) : 0;
  };
  const yTitle = `${metric === 'cumulative' ? 'Cumulative' : 'Quarterly'} Revenue (USD)`;

  const hasData = points.length > 0 && series.length > 0;
  const hasNotAvailable = notAvailable.size > 0;

  return (
    <section className={styles.card}>
      {/* Header: title + region/platform selects (same Select as the Leaderboard) */}
      <header className={styles.header}>
        <h3 className={styles.title}>Revenue Over Time</h3>
        <div className={styles.chips}>
          <Select
            value={regionFilter}
            options={regionSelectOptions}
            onChange={setRegionFilter}
            icon={<Globe size={14} />}
            onClear={() => setRegionFilter('')}
            label="Region"
            highlightWhenSet
            autoWidth
            className={styles.scopeSelect}
          />
          <Select
            value={platformFilter}
            options={platformSelectOptions}
            onChange={setPlatformFilter}
            searchable
            icon={platformFilter ? <PlatformIcon name={platformFilter} size={18} /> : <MonitorPlay size={14} />}
            onClear={() => setPlatformFilter('')}
            label="Platform"
            highlightWhenSet
            autoWidth
            className={styles.scopeSelect}
          />
        </div>
      </header>

      <div className={styles.box}>
        {/* Narrative summary */}
        <ChartSummary className={styles.summary}>
          <Emph>{title}</Emph> generated <Emph>{formatUsdCompact(headlineTotal)}</Emph> in{' '}
          {metric === 'cumulative' ? (
            <>
              cumulative revenue from <Emph>{firstDataQ ? prettyQuarter(firstDataQ) : '—'}</Emph> to{' '}
              <Emph>{lastQ ? prettyQuarter(lastQ) : '—'}</Emph>
            </>
          ) : (
            <>
              quarterly revenue in <Emph>{lastQ ? prettyQuarter(lastQ) : '—'}</Emph>
            </>
          )}{' '}
          on{' '}
          {platformFilter ? (
            <Emph>{shortPlatform(platformFilter)}</Emph>
          ) : (
            <>
              all platforms (<Emph>{platformOptions.map(shortPlatform).join(', ')}</Emph>)
            </>
          )}{' '}
          in{' '}
          {regionFilter ? (
            <Emph>{regionFilter}</Emph>
          ) : (
            <>
              all regions (<Emph>{regionOptions.join(', ')}</Emph>)
            </>
          )}
          .{' '}
          {rankWindow?.ownRank ? (
            <>
              During this period, <Emph>{title}</Emph> ranked <Emph>#{rankWindow.ownRank}</Emph> by{' '}
              {metric === 'cumulative' ? 'cumulative' : 'quarterly'} revenue among all titles on these
              platforms and regions.
            </>
          ) : null}
        </ChartSummary>

        {/* Breakdown / Metric — a ruled toolbar between the sentence and the chart
            (d360 .header). View options, not filters: neutral field style. */}
        <div className={styles.filterRow}>
          <div className={styles.toggleGroup}>
            <span className={styles.filterLabel}>Breakdown by</span>
            <SegmentedControl
              variant="field"
              options={BREAKDOWN_OPTS}
              value={breakdown}
              onChange={(v) => setBreakdown(v as Breakdown)}
            />
          </div>
          <span className={styles.toolbarDivider} aria-hidden="true" />
          <div className={styles.toggleGroup}>
            <span className={styles.filterLabel}>Metric</span>
            <SegmentedControl
              variant="field"
              options={METRIC_OPTS}
              value={metric}
              onChange={(v) => setMetric(v as RevenueMetric)}
            />
          </div>
        </div>

        <div className={styles.visualization}>
          <div className={styles.chartCol}>
            {/* Quarter range slider */}
            {allPoints.length > 1 && (
              <QuarterRangeSlider count={allPoints.length} value={[lo, hi]} onChange={setRange} labels={allLabels} />
            )}

            {hasData ? (
              <div className={styles.chartArea}>
                <div className={styles.plot}>
                {/* Own column, so it can never sit on the tick labels (d360 YAxisLabels). */}
                <span className={styles.yAxisTitle}>{yTitle}</span>
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart data={points} margin={{ top: 24, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="0" vertical={false} />
                    <XAxis
                      dataKey="label"
                      axisLine={{ stroke: 'var(--border-default)' }}
                      tickLine={false}
                      interval={0}
                      height={40}
                      tick={QuarterTick}
                    />
                    <YAxis
                      tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={52}
                      tickFormatter={(v) => formatUsdCompact(Number(v))}
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--bg-hover)', opacity: 0.4 }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const total = payload.reduce((a, p) => a + (Number(p.value) || 0), 0);
                        return (
                          <ChartTooltip
                            title={label}
                            rows={payload
                              .slice()
                              .reverse()
                              .map((p) => ({
                                label: seriesLabel(String(p.dataKey)),
                                value: formatUsd(Number(p.value)),
                                color: String(p.color),
                              }))}
                            totals={[{ label: 'Total', value: formatUsd(total) }]}
                          />
                        );
                      }}
                    />
                    {series.map((name, i) => (
                      <Bar
                        key={name}
                        dataKey={name}
                        stackId="rev"
                        radius={i === series.length - 1 ? [2, 2, 0, 0] : undefined}
                      >
                        {points.map((p) => {
                          const na = notAvailable.has(`${p.quarter}-${name}`);
                          return <Cell key={p.quarter} fill={na ? `url(#na-hatch)` : colorAt(i)} />;
                        })}
                        {/* Segment value, inside the segment when there's room. */}
                        {showAllLabels && (
                          <LabelList
                            dataKey={name}
                            content={(props) => {
                              const { x, y, width, height, value } = props as {
                                x: number; y: number; width: number; height: number; value: number;
                              };
                              if (!value || height < SEGMENT_LABEL_MIN_H || width < 28) return null;
                              return (
                                <text
                                  x={x + width / 2}
                                  y={y + height / 2}
                                  className={styles.segmentLabel}
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                >
                                  {formatUsdCompact(value)}
                                </text>
                              );
                            }}
                          />
                        )}
                        {/* Stack total above the bar — on the top series only. */}
                        {i === series.length - 1 && (
                          <LabelList
                            dataKey="label"
                            content={(props) => {
                              const { x, y, width, value } = props as {
                                x: number; y: number; width: number; value: string;
                              };
                              if (!showAllLabels && value !== lastLabel) return null;
                              const total = totalAt(value);
                              if (!total) return null;
                              return (
                                <text x={x + width / 2} y={y - 6} className={styles.totalLabel} textAnchor="middle">
                                  {formatUsdCompact(total)}
                                </text>
                              );
                            }}
                          />
                        )}
                      </Bar>
                    ))}
                    <defs>
                      <pattern id="na-hatch" width="6" height="6" patternTransform="rotate(135)" patternUnits="userSpaceOnUse">
                        <rect width="6" height="6" fill="var(--bg-elevated)" />
                        <line x1="0" y1="0" x2="0" y2="6" stroke={naColor} strokeWidth="2" />
                      </pattern>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
                </div>

                {/* Bottom-centered legend incl. Not Available */}
                <ChartLegend
                  items={[
                    ...series.map((name, i) => ({ label: seriesLabel(name), color: colorAt(i) })),
                    ...(hasNotAvailable ? [{ label: 'Not Available', color: 'var(--text-disabled)', shape: 'hatch' as const }] : []),
                  ]}
                />
              </div>
            ) : (
              <div className={styles.empty}>No revenue data available</div>
            )}
          </div>

          <NeighborRankTable
            heading="Revenue & Rank"
            headingHint={
              <Hint
                content="Revenue and rank use the selected platforms and regions. Rank is where this title's revenue stands among all titles within those filters."
                placement="top"
              >
                <Info size={12} className={styles.hintIcon} />
              </Hint>
            }
            rows={neighborRows}
            activeId={id}
            maxRevenue={maxRevenue}
            scopeLabel={scopeLabel}
            loading={rankLoading}
            emptyLabel="Ranking unavailable"
          />
        </div>
      </div>
    </section>
  );
}

/** Two-line quarter tick ("Q1" over "2020"), d360-style — no angled labels. */
function QuarterTick(props: { x?: number; y?: number; payload?: { value: string } }) {
  const { x = 0, y = 0, payload } = props;
  const [q, year] = String(payload?.value ?? '').split(' ');
  return (
    <text x={x} y={y + 12} textAnchor="middle" className={styles.quarterTick}>
      <tspan x={x}>{q}</tspan>
      {year && (
        <tspan x={x} dy={13} className={styles.quarterTickYear}>
          {year}
        </tspan>
      )}
    </text>
  );
}
